import { useEffect, useRef, useState } from "react";

import { useQuieto } from "@/hooks/useQuieto";
import { alcanza } from "@/lib/cursorQueAcompana.mjs";

// Elementos que deben teñir el cursor de rojo (clickables)
const INTERACTIVE =
  'a, button, [role="button"], input, textarea, select, label, summary, ' +
  '[data-clickable], .cursor-pointer, [onclick]';

function relativeLuminance(r, g, b) {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function parseRgb(color) {
  const m = color.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const [r, g, b, a] = m[1].split(",").map((n) => parseFloat(n));
  if (a === 0) return null;
  return { r, g, b };
}

function backgroundAt(x, y) {
  let el = document.elementFromPoint(x, y);
  while (el) {
    const bg = getComputedStyle(el).backgroundColor;
    const rgb = parseRgb(bg);
    if (rgb) return rgb;
    el = el.parentElement;
  }
  return { r: 255, g: 255, b: 255 };
}

export function useCustomCursor() {
  // MANDA EL AJUSTE Y NO SOLO EL APARATO. Aquí se preguntaba `prefers-reduced-motion` a secas,
  // así que quien había elegido «Movimiento: reducido» en Ajustes seguía teniendo un cursor con
  // inercia. La regla vive en `lib/movimiento.mjs`.
  const quieto = useQuieto();
  const [enabled, setEnabled] = useState(false);
  const dotRef = useRef(null);
  const haloRef = useRef(null);

  useEffect(() => {
    // Con "Reducir movimiento" activo mantenemos el cursor visible pero sin inercia
    // (la preferencia pide eliminar animaciones, no elementos). Solo se desactiva en táctil.
    const reducedMotion = quieto;
    const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
    if (isCoarsePointer) {
      setEnabled(false);
      return;
    }
    setEnabled(true);
    // CUÁNTO SE ACERCA EL HALO AL PUNTO EN CADA FOTOGRAMA. El resto de la cuenta —y el tope,
    // que es lo que impide que se queden a media pantalla— vive en `lib/cursorQueAcompana.mjs`.
    const suave = reducedMotion ? 1 : undefined;

    const pos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const target = { x: pos.x, y: pos.y };
    let raf = null;
    let lastThemeCheck = 0;

    const onMove = (e) => {
      target.x = e.clientX;
      target.y = e.clientY;
    };
    window.addEventListener("mousemove", onMove, { passive: true });

    const loop = (t) => {
      const paso = alcanza(pos, target, suave === undefined ? {} : { suave });
      pos.x = paso.x;
      pos.y = paso.y;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${target.x}px, ${target.y}px, 0) translate(-50%, -50%)`;
      }
      if (haloRef.current) {
        haloRef.current.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`;
      }
      if (t - lastThemeCheck > 60) {
        lastThemeCheck = t;
        const { r, g, b } = backgroundAt(target.x, target.y);
        const isLight = relativeLuminance(r, g, b) > 0.5;
        const theme = isLight ? "dark" : "light";
        // ¿el puntero está sobre un elemento interactivo? → cursor rojo
        const el = document.elementFromPoint(target.x, target.y);
        const hover = !!(el && el.closest && el.closest(INTERACTIVE));
        [haloRef.current, dotRef.current].forEach((node) => {
          if (!node) return;
          if (node.dataset.theme !== theme) node.dataset.theme = theme;
          const hv = hover ? "true" : "false";
          if (node.dataset.hover !== hv) node.dataset.hover = hv;
        });
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    document.documentElement.classList.add("custom-cursor-active");

    return () => {
      window.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
      document.documentElement.classList.remove("custom-cursor-active");
    };
  }, [quieto]);

  return { enabled, dotRef, haloRef };
}
